// Libs
import classNames from 'classnames/bind';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, AutocompleteInputChangeReason, Stack, TextField } from '@mui/material';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseOption } from '~/utils/interface/common';
import { varColor } from '~/themes/themeVariables';
// Styles, images, icons
import styles from './BaseAutocomplete.module.scss';
import { icons } from '~/assets';

type Props<T> = {
  width?: number | string;
  height?: number | string;
  required?: boolean;
  isLoading?: boolean;
  multiple?: boolean;
  options: ReadonlyArray<T>;
  showIcon?: boolean;
  value?: T | T[] | null;
  label?: string;
  errorMessage?: string;
  placeholder: string;
  textSearch?: string;
  onChange?: (value: T | T[] | null) => void;
  onTextSearchChange?: (value: string) => void;
};

const cx = classNames.bind(styles);

const BaseAutocomplete = <T extends IBaseOption>(props: Props<T>) => {
  //#region Destructuring Props
  const {
    showIcon = true,
    required = false,
    label,
    isLoading,
    errorMessage,
    multiple,
    width = '100%',
    height = 36,
    options,
    value,
    placeholder,
    textSearch,
    onChange,
    onTextSearchChange,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [autocompleteValue, setAutocompleteValue] = useState<T | T[] | null>(multiple ? [] : null);
  const [inputValue, setInputValue] = useState<string>(EMPTY_STRING);
  //#endregion Declare State

  //#region Implement Hook
  useEffect(() => {
    if (value) {
      setAutocompleteValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (!textSearch) {
      setInputValue(EMPTY_STRING);
      return;
    }

    setInputValue(textSearch);
  }, [textSearch]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleAutocompleteChange = (event: React.SyntheticEvent, newValue: T | T[] | null) => {
    textSearch && setAutocompleteValue(newValue);
    onChange && onChange(newValue);
    !textSearch && setInputValue(EMPTY_STRING);
  };

  const handleInputChange = (event: React.SyntheticEvent, value: string, reason: AutocompleteInputChangeReason) => {
    if (reason === 'input' || reason === 'selectOption') {
      setInputValue(value);
      onTextSearchChange && onTextSearchChange(value);
    }
  };

  const generateLabel = (option: T) => {
    if (option.label) {
      return option.label.toString();
    }

    return EMPTY_STRING;
  };
  //#endregion Handle Function

  return (
    <Stack id='baseAutocompleteComponent' className={cx('container')} gap={2}>
      {label && <BaseLabel label={label} required={required} />}

      <Autocomplete
        multiple={multiple}
        disableCloseOnSelect={multiple}
        size='small'
        className={cx('autocomplete')}
        id={'autocompleteComponent'}
        options={options || []}
        getOptionLabel={(option: T) => generateLabel(option)}
        value={autocompleteValue}
        onChange={handleAutocompleteChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        renderOption={(props, option, { index }) => (
          <li {...props} key={`${option.value}-${index}`}>
            {generateLabel(option)}
          </li>
        )}
        isOptionEqualToValue={(option, value) => {
          return option.value.toString() === value.value.toString();
        }}
        renderTags={() => null}
        slotProps={{
          popper: {
            className: cx('popper'),
          },
          paper: {
            className: cx('paper'),
          },
          listbox: {
            className: cx('listBox'),
            sx: {
              '.MuiAutocomplete-option': {
                borderRadius: '8px',
                fontSize: '12px',
                color: varColor.textSelectOption,
              },
              '.MuiAutocomplete-option.Mui-focused': {
                backgroundColor: 'transparent',
              },
              '.MuiAutocomplete-option[aria-selected="false"]:hover': {
                backgroundColor: varColor.bgHover,
              },
              '.MuiAutocomplete-option[aria-selected="true"]': {
                backgroundColor: varColor.secondary,
              },
              '.MuiAutocomplete-option[aria-selected="true"].Mui-focused': {
                backgroundColor: varColor.secondary,
              },
            },
          },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            className={cx('input')}
            placeholder={isLoading ? t('common_button_loading') : placeholder}
            sx={{
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
                width,
                height,
                backgroundColor: 'white',
              },
              '.MuiOutlinedInput-root.MuiInputBase-sizeSmall .MuiAutocomplete-input': {
                color: varColor.textPrimary,
              },
            }}
            InputProps={{
              ...params.InputProps,
              // endAdornment: <img src={icons.commonIconArrowBottom} alt={t('common_img_text_alt')} />,
              startAdornment: isLoading ? (
                <div className={cx('viewLoading')}>
                  <span className={cx('loadingContent')} />
                </div>
              ) : (
                showIcon && (
                  <img src={icons.commonIconSearch} alt={t('common_img_text_alt')} className={cx('searchIcon')} />
                )
              ),
            }}
          />
        )}
        popupIcon={null}
        onClose={(event) => {
          event.preventDefault();
        }}
        noOptionsText={
          <div className={cx('noOptions')}>{t('common_label_no_data_available', { ns: 'common/base' })}</div>
        }
      />

      {errorMessage && <span className={cx('errMessage')}>{errorMessage}</span>}
    </Stack>
  );
};

export default BaseAutocomplete;
